// A dropped file → something an <audio> element can play, for previewing the
// tracks before they go up. Nothing leaves the tab.
//
// The file itself when the browser plays its format. Chrome and Firefox don't
// play AIFF (Live's own renders), so an AIFF there is decoded by the pipeline's
// own reader (shared/audio/master-mix.ts) and rewrapped as a 16-bit WAV; what
// that reader can't (compressed AIFC) the browser decodes, as an upload would.

import { decodeAudio } from "@shared/audio/master-mix.js";
import type { IntakeFile } from "./intake";
import { decodeFile } from "./to-wav";
import { encodePcmWav } from "./wav-out";

const MIME: Record<string, string> = {
  wav: "audio/wav",
  wave: "audio/wav",
  aif: "audio/aiff",
  aiff: "audio/aiff",
  aifc: "audio/aiff",
  flac: "audio/flac",
  mp3: "audio/mpeg",
};

/** Whether this browser's <audio> plays the extension as it is. */
export function playsAsIs(ext: string, canPlayType: (mime: string) => string): boolean {
  const mime = MIME[ext];
  return mime !== undefined && canPlayType(mime) !== "";
}

/** An object URL for the file, playable here. The caller revokes it. Throws on audio it can't read. */
export async function previewUrl(file: IntakeFile): Promise<string> {
  const probe = document.createElement("audio");
  if (playsAsIs(file.ext, (m) => probe.canPlayType(m))) return URL.createObjectURL(file.blob);
  const decoded = decodeAudio(new Uint8Array(await file.blob.arrayBuffer()));
  const wav = decoded
    ? encodePcmWav(
        {
          sampleRate: decoded.sampleRate,
          numberOfChannels: decoded.channels,
          length: decoded.frames,
          getChannelData: (c) => {
            const out = new Float32Array(decoded.frames);
            for (let f = 0; f < decoded.frames; f++) out[f] = decoded.samples[f * decoded.channels + c]!;
            return out;
          },
        },
        16,
      )
    : encodePcmWav(await decodeFile(file.blob, file.ext, null), 16);
  return URL.createObjectURL(new Blob([wav as Uint8Array<ArrayBuffer>], { type: "audio/wav" }));
}
